import { useCallback, useEffect, useState } from 'react'
import { Ban, CheckCircle2, LoaderCircle, RefreshCw, Search, ShieldCheck, UserRound, Users } from 'lucide-react'
import { getFirebaseIdToken, useAuthSession } from '../../lib/firebase'
import './admin.css'

type UserStatus = 'active' | 'blocked'
interface ManagedUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  status: UserStatus
  firstSeenAt: string
  lastLoginAt: string
}

async function adminRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getFirebaseIdToken()
  const headers = new Headers(init?.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init?.body) headers.set('content-type', 'application/json')
  const response = await fetch(path, { ...init, headers })
  const payload = await response.json().catch(() => ({})) as T & { message?: string }
  if (!response.ok) throw new Error(payload.message || `تعذر إكمال الطلب (رمز ${response.status}).`)
  return payload
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('ar-EG', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

export function AdminUsersPage() {
  const session = useAuthSession()
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | UserStatus>('all')
  const [loading, setLoading] = useState(true)
  const [busyUid, setBusyUid] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const query = new URLSearchParams({ pageSize: '100' })
      if (search.trim()) query.set('search', search.trim())
      if (filter !== 'all') query.set('status', filter)
      const payload = await adminRequest<{ users: ManagedUser[] }>(`/api/admin/users?${query}`)
      setUsers(payload.users)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'تعذر تحميل المستخدمين.')
    } finally {
      setLoading(false)
    }
  }, [filter, search])

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250)
    return () => window.clearTimeout(timer)
  }, [load])

  async function changeStatus(user: ManagedUser) {
    const status: UserStatus = user.status === 'active' ? 'blocked' : 'active'
    if (status === 'blocked' && !window.confirm(`إيقاف دخول ${user.email || user.displayName || 'هذا المستخدم'}؟`)) return
    setBusyUid(user.uid)
    setError(null)
    try {
      const payload = await adminRequest<{ user: ManagedUser }>(`/api/admin/users/${encodeURIComponent(user.uid)}`, {
        method: 'PATCH', body: JSON.stringify({ status }),
      })
      setUsers((current) => current.map((entry) => entry.uid === user.uid ? payload.user : entry))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'تعذر تحديث المستخدم.')
    } finally {
      setBusyUid(null)
    }
  }

  return (
    <section className="admin-users" aria-labelledby="admin-users-title">
      <header className="admin-users__header">
        <div><span>إعدادات الوصول</span><h1 id="admin-users-title">إدارة المستخدمين</h1><p>راجع حسابات المستخدمين وتحكّم في إمكانية دخولهم إلى المنصة.</p></div>
        <button type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={17} />تحديث</button>
      </header>

      <div className="admin-users__summary">
        <article><Users size={20} /><div><strong>{users.length}</strong><span>إجمالي النتائج</span></div></article>
        <article><CheckCircle2 size={20} /><div><strong>{users.filter((user) => user.status === 'active').length}</strong><span>نشط</span></div></article>
        <article><Ban size={20} /><div><strong>{users.filter((user) => user.status === 'blocked').length}</strong><span>موقوف</span></div></article>
      </div>

      <div className="admin-users__filters">
        <label><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ابحث بالاسم أو البريد" /></label>
        <select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)} aria-label="تصفية المستخدمين">
          <option value="all">كل المستخدمين</option><option value="active">النشطون</option><option value="blocked">الموقوفون</option>
        </select>
      </div>

      {error && <p className="admin-users__error" role="alert">{error}</p>}
      {loading ? <div className="admin-users__loading"><LoaderCircle className="spin" />جارٍ تحميل المستخدمين…</div> : users.length === 0 ? (
        <div className="admin-users__empty"><UserRound size={28} /><strong>لا توجد حسابات مطابقة</strong><span>سيظهر المستخدم هنا بعد تسجيل دخوله إلى المنصة للمرة الأولى.</span></div>
      ) : (
        <div className="admin-users__list">
          {users.map((user) => {
            const protectedAccount = user.uid === session.user?.uid
            return (
              <article className="admin-user" key={user.uid}>
                {user.photoURL ? <img src={user.photoURL} alt="" /> : <span className="admin-user__avatar"><UserRound size={20} /></span>}
                <div className="admin-user__identity"><strong>{user.displayName || 'الاسم غير متوفر'}</strong><span>{user.email || 'البريد غير متوفر'}</span></div>
                <div className="admin-user__dates"><span>آخر دخول</span><strong>{formatDate(user.lastLoginAt)}</strong><small>أول دخول: {formatDate(user.firstSeenAt)}</small></div>
                <span className={`admin-user__status admin-user__status--${user.status}`}>{user.status === 'active' ? 'نشط' : 'موقوف'}</span>
                <button className={`admin-user__action admin-user__action--${user.status}`} type="button" disabled={busyUid === user.uid || protectedAccount} onClick={() => void changeStatus(user)} title={protectedAccount ? 'لا يمكنك إيقاف حسابك الحالي' : undefined}>
                  {busyUid === user.uid ? <LoaderCircle className="spin" size={16} /> : user.status === 'active' ? <Ban size={16} /> : <ShieldCheck size={16} />}
                  {user.status === 'active' ? 'إيقاف الدخول' : 'السماح بالدخول'}
                </button>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
